import Checklist from "Pages/GcPrecon/GcProjectDetails/components/Checklist";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import ProgressBar from "Components/Common/ProgressBar/ProgressBar";
import SectionHeader from "Pages/GcPrecon/GcProjectDetails/components/SectionHeader";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyPreconReportIcon from "Assets/Img/EmptyPreconReportIcon";
import {
  commercialDocLabels,
  commercialQAQuestions,
} from "Pages/GcPrecon/GcPreconCommercial/preconConfig";
import "./PreconChecklistSection.scss";

interface ChecklistItem {
  id: string;
  key: string;
  value: unknown;
  isPublished: boolean;
  uploadedAt: string;
}

interface ChecklistData {
  documents: ChecklistItem[];
  questions: ChecklistItem[];
  totalItemCount: number;
  answered: number;
}

interface Props {
  data: ChecklistData | null;
  loading: boolean;
  notFound?: boolean;
}

const DOC_LABEL: Record<string, string> = Object.fromEntries(
  commercialDocLabels.map((d) => [
    d.key,
    d.extra ? `${d.label} ${d.extra}` : d.label,
  ]),
);

const QA_LABEL: Record<string, string> = Object.fromEntries(
  commercialQAQuestions.map((q) => [q.key, q.label]),
);

function formatUploadedAt(dateStr: string): string {
  return `Uploaded · ${new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })}`;
}

function formatQAValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const v = value as Record<string, unknown>;
    if ("amount" in v) return `$${Number(v.amount).toLocaleString()}`;
    if ("total_sqft" in v)
      return `${Number(v.total_sqft).toLocaleString()} sq ft`;
  }
  return String(value);
}

export default function PreconChecklistSection({
  data,
  loading,
  notFound,
}: Props) {
  if (notFound) {
    return (
      <div className="ds active" id="ds-precon-checklist">
        <EmptyState
          icon={<EmptyPreconReportIcon />}
          title="Pre-Construction Checklist Not Available"
          subtitle="The GC has not yet submitted the pre-construction checklist for this project. Check back once it has been completed."
        />
      </div>
    );
  }
  const answered = data?.answered ?? 0;
  const total = data?.totalItemCount ?? 0;
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;

  const documents = (data?.documents ?? []).map((item) => {
    const fileVal =
      item.value != null
        ? (item.value as { key: string; fileName: string })
        : null;
    return {
      id: item.id,
      label: DOC_LABEL[item.key] ?? item.key,
      status: fileVal ? "complete" : "pending",
      icon: fileVal ? "✓" : "!",
      sub: fileVal ? formatUploadedAt(item.uploadedAt) : "Not yet uploaded",
      action: fileVal ? () => window.open(fileVal.key, "_blank") : undefined,
      buttonLabel: "View",
    };
  });

  const qaItems = (data?.questions ?? []).map((item) => ({
    id: item.id,
    label: QA_LABEL[item.key] ?? item.key,
    status: item.value != null ? "complete" : "pending",
    icon: item.value != null ? "✓" : "!",
    sub: item.value != null ? `Response: ${formatQAValue(item.value)}` : "",
  }));

  return (
    <div
      className="preconChecklistSection section active"
      id="sec-precon-checklist"
    >
      <SectionHeader
        title="Pre-Construction Checklist"
        subtitle="Required documentation and Q&A submitted by the GC before the first draw."
      />

      <div className="card">
        {loading ? (
          <>
            <Skeleton width={200} height={14} style={{ marginBottom: 8 }} />
            <Skeleton width="100%" height={8} borderRadius={4} />
          </>
        ) : (
          <>
            <div className="progRow">
              <div className="overallComplete">Overall Completion</div>
              <span className="progVal">
                {answered} of {total} complete
              </span>
            </div>
            <ProgressBar value={pct} />
          </>
        )}
      </div>

      {loading ? (
        <div className="card">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton
              key={i}
              height={36}
              borderRadius={6}
              style={{ marginBottom: 8 }}
            />
          ))}
        </div>
      ) : (
        <Checklist title="Required Documents" items={documents} />
      )}

      {loading ? (
        <div className="card">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              height={36}
              borderRadius={6}
              style={{ marginBottom: 8 }}
            />
          ))}
        </div>
      ) : (
        <Checklist title="Pre-Construction Q&A" items={qaItems} />
      )}
    </div>
  );
}
